"use client";
import React from "react";
import {
  useScroll,
  useTransform,
  useSpring,
  MotionValue,
  m
} from "motion/react";
import Image from "next/image";

export const HeroParallax = ({
  products,
}: {
  products: {
    title: string;
    thumbnail: string;
  }[];
}) => {
  const firstRow = products.slice(0, 5);
  const secondRow = products.slice(5, 10);
  const thirdRow = products.slice(10, 15);
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const springConfig = { stiffness: 300, damping: 30, bounce: 100 };

  const translateXslow = useSpring(
    useTransform(scrollYProgress, [1, 0], [0, 1400]),
    springConfig
  );
  const translateXmedium = useSpring(
    useTransform(scrollYProgress, [1, 0], [0, -500]),
    springConfig
  );
  const translateXfast = useSpring(
    useTransform(scrollYProgress, [0, 1], [0, 300]),
    springConfig
  );
  const rotateX = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [isMobile ? 0 : 15, 0]),
    springConfig
  );
  const opacity = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [0.2, 1]),
    springConfig
  );
  const rotateZ = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [isMobile ? 0 : 20, 0]),
    springConfig
  );
  const translateY = useSpring(
    useTransform(scrollYProgress, [0, 0.2], [isMobile ? -100 : -700, isMobile ? 100 : 500]),
    springConfig
  );
  return (
    <div
      ref={ref}
      className="z-40 h-[300vh] py-40 overflow-visible  antialiased relative flex flex-col self-auto [perspective:1000px] [transform-style:preserve-3d]"
    >
      <Header />
      <m.div
        style={{
          rotateX,
          rotateZ,
          translateY,
          opacity,
        }}
        className=""
      >
        <m.div className="flex flex-row-reverse space-x-reverse space-x-20 mb-20">
          {firstRow.map((product, idx) => (
            <ProductCard
              product={product}
              translate={translateXslow}
              key={`first-${idx}`}
            />
          ))}
        </m.div>
        <m.div className="flex flex-row  mb-20 space-x-20 ">
          {secondRow.map((product, idx) => (
            <ProductCard
              product={product}
              translate={translateXmedium}
              key={`second-${idx}`}
            />
          ))}
        </m.div>
        <m.div className="flex flex-row-reverse space-x-reverse space-x-20">
          {thirdRow.map((product, idx) => (
            <ProductCard
              product={product}
              translate={translateXfast}
              key={`third-${idx}`}
            />
          ))}
        </m.div>
      </m.div>
    </div>
  );
};

export const Header = () => {
  return (
    <div className="max-w-7xl relative mx-auto py-20 md:py-40 px-4 w-full  left-0 top-0">
      <h1 className="text-3xl sm:text-4xl md:text-7xl lg:7xl font-regular dark:text-white">
        Transformando o Bom <br /> em
        <span className="ml-2 bg-gradient-to-br from-midas-yellow-400 via-midas-yellow-300 to-midas-yellow-700 bg-clip-text text-transparent animate-shine-delayed will-change-[background-position]">
          Extraordinário
        </span>
      </h1>
      <p className="max-w-2xl text-base md:text-xl mt-8 dark:text-neutral-200">
        No ecossistema Midas, cada detalhe é pensado para transformar o comum em
        extraordinário. Imagine seu cliente não apenas satisfeito, mas encantado
      </p>
    </div>
  );
};

export const ProductCard = ({
  product,
  translate,
}: {
  product: {
    title: string;
    thumbnail: string;
  };
  translate: MotionValue<number>;
}) => {
  return (
    <m.div
      style={{
        x: translate,
      }}
      whileHover={{
        y: -20,
      }}
      className="group/product shadow-slate-800/50 shadow-2xl rounded-3xl overflow-hidden h-96 w-[30rem] relative flex-shrink-0"
    >
      <div className="block group-hover/product:shadow-2xl ">
        <Image
          src={product.thumbnail}
          height="600"
          width="600"
          className="object-cover object-center-top absolute h-full w-full inset-0 rounded-3xl"
          alt={product.title}
        />
      </div>
      <div className="absolute inset-0 h-full w-full opacity-0 group-hover/product:opacity-80 bg-black pointer-events-none"></div>
      <h2 className="absolute bottom-4 left-4 opacity-0 group-hover/product:opacity-100 text-white">
        {product.title}
      </h2>
    </m.div>
  );
};
